/**
 * 工单管理 — GET /api/admin/tickets（列表） + /api/admin/tickets/:id（详情 & 回复）
 *
 * 单页内完成 master/detail：表格点击一行打开右侧 Drawer，Drawer 内显示
 * 工单元数据 + 回复时间线 + 管理员回复框；PATCH 可改状态/优先级/分派，POST /reply
 * 新增回复，POST /resolve 关单。
 */
import { useState } from 'react';
import {
  Button,
  Descriptions,
  Drawer,
  Form,
  Input,
  Select,
  Space,
  Tag,
  Timeline,
  message,
} from 'antd';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { DataTable } from '../../components/DataTable';
import { api, ApiError } from '../../api/client';
import { useAuthStore } from '../../store/auth';

type TicketStatus = 'open' | 'pending_user' | 'pending_admin' | 'resolved' | 'closed';
type TicketPriority = 'low' | 'normal' | 'high' | 'urgent';

interface TicketRow {
  id: number;
  user_id: number;
  category: string;
  title: string;
  body: string;
  status: TicketStatus;
  priority: TicketPriority;
  assigned_to: number | null;
  resolved_at: string | null;
  create_time: string;
  update_time: string;
}

interface TicketReply {
  id: number;
  ticket_id: number;
  sender_type: 'user' | 'admin';
  sender_id: number;
  body: string;
  create_time: string;
}

interface TicketDetail {
  ticket: TicketRow;
  replies: TicketReply[];
}

function statusTag(s: TicketStatus) {
  const map: Record<TicketStatus, { color: string; text: string }> = {
    open: { color: 'blue', text: '新建' },
    pending_user: { color: 'orange', text: '待用户' },
    pending_admin: { color: 'red', text: '待处理' },
    resolved: { color: 'green', text: '已解决' },
    closed: { color: 'default', text: '已关闭' },
  };
  const { color, text } = map[s];
  return <Tag color={color}>{text}</Tag>;
}

function priorityTag(p: TicketPriority) {
  const map: Record<TicketPriority, { color: string; text: string }> = {
    low: { color: 'default', text: '低' },
    normal: { color: 'blue', text: '普通' },
    high: { color: 'orange', text: '高' },
    urgent: { color: 'red', text: '紧急' },
  };
  const { color, text } = map[p];
  return <Tag color={color}>{text}</Tag>;
}

export function TicketPage() {
  const qc = useQueryClient();
  const [status, setStatus] = useState<TicketStatus | undefined>();
  const [priority, setPriority] = useState<TicketPriority | undefined>();
  const [keyword, setKeyword] = useState('');
  const [openId, setOpenId] = useState<number | null>(null);
  const [replyForm] = Form.useForm<{ body: string }>();

  const hasNode = useAuthStore((s) => s.hasNode);
  const canEdit = hasNode('platform.ticket.edit');
  const canReply = hasNode('platform.ticket.reply');
  const canResolve = hasNode('platform.ticket.resolve');

  const detailQuery = useQuery({
    queryKey: ['admin', 'ticket', openId],
    queryFn: () => api.get<TicketDetail>(`/api/admin/tickets/${openId}`),
    enabled: openId !== null,
  });

  function invalidateAll() {
    qc.invalidateQueries({ queryKey: ['admin', 'tickets'] });
    if (openId !== null) qc.invalidateQueries({ queryKey: ['admin', 'ticket', openId] });
  }

  async function updateTicket(patch: Partial<Pick<TicketRow, 'status' | 'priority' | 'assigned_to'>>) {
    if (openId === null) return;
    try {
      await api.patch(`/api/admin/tickets/${openId}`, patch);
      message.success('已更新');
      invalidateAll();
    } catch (e) {
      message.error(e instanceof ApiError ? e.message : '更新失败');
    }
  }

  async function onReply(values: { body: string }) {
    if (openId === null) return;
    try {
      await api.post(`/api/admin/tickets/${openId}/reply`, { body: values.body });
      message.success('已回复');
      replyForm.resetFields();
      invalidateAll();
    } catch (e) {
      message.error(e instanceof ApiError ? e.message : '回复失败');
    }
  }

  async function resolveTicket() {
    if (openId === null) return;
    try {
      await api.post(`/api/admin/tickets/${openId}/resolve`);
      message.success('已标记为解决');
      invalidateAll();
    } catch (e) {
      message.error(e instanceof ApiError ? e.message : '操作失败');
    }
  }

  const columns = [
    { title: 'ID', dataIndex: 'id', width: 70 },
    { title: '用户', dataIndex: 'user_id', width: 90 },
    { title: '分类', dataIndex: 'category', width: 100, render: (v: string) => <Tag>{v}</Tag> },
    {
      title: '标题',
      dataIndex: 'title',
      ellipsis: true,
      render: (v: string, row: TicketRow) => (
        <a onClick={() => setOpenId(row.id)}>{v}</a>
      ),
    },
    { title: '优先级', dataIndex: 'priority', width: 90, render: priorityTag },
    { title: '状态', dataIndex: 'status', width: 100, render: statusTag },
    { title: '创建时间', dataIndex: 'create_time', width: 170 },
  ];

  const ticket = detailQuery.data?.ticket;
  const replies = detailQuery.data?.replies ?? [];

  return (
    <div>
      <h2 style={{ marginTop: 0 }}>用户留言 / 工单</h2>
      <Space style={{ marginBottom: 12 }} wrap>
        <Select
          placeholder="状态"
          style={{ width: 140 }}
          allowClear
          value={status}
          onChange={setStatus}
          options={[
            { value: 'open', label: '新建' },
            { value: 'pending_user', label: '待用户' },
            { value: 'pending_admin', label: '待处理' },
            { value: 'resolved', label: '已解决' },
            { value: 'closed', label: '已关闭' },
          ]}
        />
        <Select
          placeholder="优先级"
          style={{ width: 120 }}
          allowClear
          value={priority}
          onChange={setPriority}
          options={[
            { value: 'low', label: '低' },
            { value: 'normal', label: '普通' },
            { value: 'high', label: '高' },
            { value: 'urgent', label: '紧急' },
          ]}
        />
        <Input
          placeholder="搜索标题/正文"
          style={{ width: 260 }}
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)}
          allowClear
        />
      </Space>

      <DataTable<TicketRow>
        queryKey={['admin', 'tickets', status ?? '', priority ?? '', keyword]}
        endpoint="/api/admin/tickets"
        extraParams={{
          status,
          priority,
          keyword: keyword.trim() || undefined,
        }}
        columns={columns}
        rowKey="id"
        scroll={{ x: 1000 }}
      />

      <Drawer
        open={openId !== null}
        title={ticket ? `工单 #${ticket.id} — ${ticket.title}` : '工单详情'}
        width={680}
        onClose={() => {
          setOpenId(null);
          replyForm.resetFields();
        }}
        loading={detailQuery.isFetching && !ticket}
      >
        {ticket && (
          <>
            <Descriptions column={2} size="small" bordered>
              <Descriptions.Item label="用户">{ticket.user_id}</Descriptions.Item>
              <Descriptions.Item label="分类">{ticket.category}</Descriptions.Item>
              <Descriptions.Item label="状态">{statusTag(ticket.status)}</Descriptions.Item>
              <Descriptions.Item label="优先级">{priorityTag(ticket.priority)}</Descriptions.Item>
              <Descriptions.Item label="分派给">{ticket.assigned_to ?? '—'}</Descriptions.Item>
              <Descriptions.Item label="创建时间">{ticket.create_time}</Descriptions.Item>
              <Descriptions.Item label="正文" span={2}>
                <pre style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{ticket.body}</pre>
              </Descriptions.Item>
            </Descriptions>

            {canEdit && (
              <Space style={{ marginTop: 16 }} wrap>
                <Select
                  size="small"
                  style={{ width: 140 }}
                  value={ticket.status}
                  onChange={(v) => updateTicket({ status: v })}
                  options={[
                    { value: 'open', label: '新建' },
                    { value: 'pending_user', label: '待用户' },
                    { value: 'pending_admin', label: '待处理' },
                    { value: 'resolved', label: '已解决' },
                    { value: 'closed', label: '已关闭' },
                  ]}
                />
                <Select
                  size="small"
                  style={{ width: 120 }}
                  value={ticket.priority}
                  onChange={(v) => updateTicket({ priority: v })}
                  options={[
                    { value: 'low', label: '低' },
                    { value: 'normal', label: '普通' },
                    { value: 'high', label: '高' },
                    { value: 'urgent', label: '紧急' },
                  ]}
                />
                {canResolve && ticket.status !== 'resolved' && ticket.status !== 'closed' && (
                  <Button size="small" type="primary" onClick={resolveTicket}>
                    标记为解决
                  </Button>
                )}
              </Space>
            )}

            <h4 style={{ marginTop: 24 }}>回复时间线</h4>
            <Timeline
              items={replies.map((r) => ({
                color: r.sender_type === 'admin' ? 'blue' : 'green',
                children: (
                  <div>
                    <div style={{ fontSize: 12, color: '#999' }}>
                      {r.sender_type === 'admin' ? '管理员' : '用户'} #{r.sender_id} ·{' '}
                      {r.create_time}
                    </div>
                    <pre style={{ whiteSpace: 'pre-wrap', margin: '4px 0 0 0' }}>{r.body}</pre>
                  </div>
                ),
              }))}
            />
            {replies.length === 0 && <div style={{ color: '#999' }}>暂无回复</div>}

            {canReply && ticket.status !== 'closed' && (
              <Form
                form={replyForm}
                layout="vertical"
                onFinish={onReply}
                style={{ marginTop: 16 }}
              >
                <Form.Item
                  name="body"
                  label="管理员回复"
                  rules={[{ required: true, min: 1, max: 10000 }]}
                >
                  <Input.TextArea rows={5} placeholder="输入回复内容…" />
                </Form.Item>
                <Button type="primary" htmlType="submit">
                  发送回复
                </Button>
              </Form>
            )}
          </>
        )}
      </Drawer>
    </div>
  );
}
